import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, CartProvider } from "@/context/AppContext";
import EmblemLoader from "@/components/EmblemLoader";
import AgeGate, { AGE_OK_KEY } from "@/components/AgeGate";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import SupportChat from "@/components/SupportChat";
import LaunchBanner from "@/components/LaunchBanner";
import WelcomeModal from "@/components/WelcomeModal";
import AbandonCartModal from "@/components/AbandonCartModal";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import { Login, Register, ForgotPassword, ResetPassword, AuthCallback } from "@/pages/Auth";
import { Account } from "@/pages/Account";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import CryptoReturn from "@/pages/CryptoReturn";
import Admin from "@/pages/Admin";
import Track from "@/pages/Track";
import { Science, Testing, About, FAQPage, Contact, Legal } from "@/pages/StaticPages";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppShell() {
  const [ready, setReady] = useState(false);
  const { pathname, hash } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const [ageOk, setAgeOk] = useState(() => isAdmin || !!localStorage.getItem(AGE_OK_KEY));
  useEffect(() => {
    if (sessionStorage.getItem("bl_intro_seen")) setReady(true);
  }, []);
  // Emergent Google OAuth returns to {origin}/account#session_id=… — handle it before any route renders.
  if (hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      {!isAdmin && <AgeGate onConfirm={() => setAgeOk(true)} />}
      {ageOk && <EmblemLoader onDone={() => setReady(true)} />}
      <div style={{ opacity: ready ? 1 : 0, transition: "opacity 700ms ease" }}>
        <LaunchBanner />
        <Nav />
        <ScrollToTop />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/accessories" element={<Shop accessories />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account" element={<Account />} />
            <Route path="/science" element={<Science />} />
            <Route path="/testing" element={<Testing />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/payment/crypto/return" element={<CryptoReturn />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/track" element={<Track />} />
            <Route path="/track/:id" element={<Track />} />
            <Route path="/legal/privacy" element={<Legal slug="privacy" />} />
            <Route path="/legal/terms" element={<Legal slug="terms" />} />
            <Route path="/legal/shipping" element={<Legal slug="shipping" />} />
            <Route path="/legal/shipping-policy" element={<Legal slug="shipping-policy" />} />
            <Route path="/legal/returns" element={<Legal slug="returns" />} />
            <Route path="/legal/disclaimer" element={<Legal slug="disclaimer" />} />
            <Route path="/legal/age-verification" element={<Legal slug="age-verification" />} />
            <Route path="/legal/cookies" element={<Legal slug="cookies" />} />
          </Routes>
        </main>
        <Footer />
        <CartDrawer />
        {!isAdmin && <SupportChat />}
        {!isAdmin && <WelcomeModal />}
        {!isAdmin && <AbandonCartModal />}
      </div>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppShell />
            <Toaster
              theme="dark"
              position={window.matchMedia("(max-width: 767px)").matches ? "top-center" : "bottom-right"}
              offset={window.matchMedia("(max-width: 767px)").matches ? 72 : 32}
              toastOptions={{
                style: {
                  background: "#061125",
                  color: "#fff",
                  border: "1px solid rgba(120,180,255,0.22)",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "12px",
                  letterSpacing: "0.05em",
                },
              }}
            />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
